import React, { useMemo } from "react";
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, Typography } from "@mui/material";

interface WorkflowDiagramProps {
  pythonCode: string;
}

interface WorkflowStep {
  id: string;
  name: string;
  type: "step" | "condition" | "action" | "start" | "end";
  details?: string;
}

interface WorkflowConnection {
  from: string;
  to: string;
  condition?: string;
}

// Parse Python code to extract action calls and branches
const parseWorkflowCode = (code: string): { steps: WorkflowStep[]; connections: WorkflowConnection[] } => {
  const steps: WorkflowStep[] = [];
  const connections: WorkflowConnection[] = [];
  
  // Add start node
  steps.push({
    id: "start",
    name: "Start",
    type: "start",
  });

  const actionSteps = new Map<string, WorkflowStep>();
  const conditionSteps = new Map<string, WorkflowStep>();
  const stepOrder: Array<{ id: string; type: string }> = [];

  // Extract platform-specific action calls
  const actionPattern = /\b(m365|azure|aws|gcp|salesforce|jira|slack|teams|google|okta|duo)_[\w]+/gi;
  
  const allActions = new Set<string>();
  let match;
  
  while ((match = actionPattern.exec(code)) !== null) {
    const actionName = match[0].toLowerCase();
    if (!allActions.has(actionName)) {
      allActions.add(actionName);
      const stepId = `action_${actionName}`;
      actionSteps.set(stepId, {
        id: stepId,
        name: actionName.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase()),
        type: "action",
      });
      stepOrder.push({ id: stepId, type: "action" });
    }
  }

  // Extract conditions (if statements) for branching logic
  const lines = code.split("\n");
  let conditionIndex = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Look for if statements that check results or conditions
    if (line.startsWith("if ") && !line.includes("__name__") && !line.includes("__main__")) {
      const conditionMatch = line.match(/if\s+([^:]+):/);
      if (conditionMatch) {
        let condition = conditionMatch[1].trim();
        
        // Simplify condition
        condition = condition
          .replace(/\(/g, "")
          .replace(/\)/g, "")
          .replace(/==\s*True/g, "")
          .replace(/==\s*False/g, " not")
          .replace(/!=\s*None/g, " exists")
          .replace(/==\s*None/g, " not exists")
          .replace(/is\s+not\s+None/g, "exists")
          .replace(/is\s+None/g, "not exists")
          .replace(/\s+/g, " ")
          .trim();
        
        // Only include meaningful conditions
        if (condition.length > 3 && condition.length < 60) {
          const conditionId = `condition_${conditionIndex}`;
          if (!conditionSteps.has(conditionId)) {
            conditionSteps.set(conditionId, {
              id: conditionId,
              name: condition.length > 30 ? condition.substring(0, 30) + "..." : condition,
              type: "condition",
              details: condition,
            });
            stepOrder.push({ id: conditionId, type: "condition" });
            conditionIndex++;
          }
        }
      }
    }
  }

  // Add all steps to the array
  Array.from(actionSteps.values()).forEach(step => steps.push(step));
  Array.from(conditionSteps.values()).forEach(step => steps.push(step));

  // Build connections with branching logic
  const processedConnections = new Set<string>();
  
  if (stepOrder.length > 0) {
    connections.push({ from: "start", to: stepOrder[0].id });
    processedConnections.add(`start-${stepOrder[0].id}`);
    
    // Analyze code flow to connect steps
    let currentStep: string | null = stepOrder[0].id;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if line contains an action
      Array.from(actionSteps.entries()).forEach(([stepId, step]) => {
        const actionName = stepId.replace("action_", "");
        const actionRegex = new RegExp(`\\b${actionName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\(`, "i");
        if (actionRegex.test(line)) {
          if (currentStep && currentStep !== stepId) {
            const connKey = `${currentStep}-${stepId}`;
            if (!processedConnections.has(connKey)) {
              connections.push({ from: currentStep, to: stepId });
              processedConnections.add(connKey);
            }
          }
          currentStep = stepId;
        }
      });
      
      // Check for conditions
      if (line.startsWith("if ")) {
        const conditionMatch = line.match(/if\s+([^:]+):/);
        if (conditionMatch) {
          // Find matching condition step
          Array.from(conditionSteps.entries()).forEach(([condId, condStep]) => {
            if (condStep.details && conditionMatch[1].includes(condStep.details.substring(0, 15))) {
              if (currentStep && !processedConnections.has(`${currentStep}-${condId}`)) {
                connections.push({ from: currentStep, to: condId });
                processedConnections.add(`${currentStep}-${condId}`);
                currentStep = condId;
              }
            }
          });
        }
      }
    }
    
    // If no explicit connections found, create sequential flow
    if (connections.length <= 1) {
      for (let i = 0; i < stepOrder.length - 1; i++) {
        const connKey = `${stepOrder[i].id}-${stepOrder[i + 1].id}`;
        if (!processedConnections.has(connKey)) {
          connections.push({ from: stepOrder[i].id, to: stepOrder[i + 1].id });
          processedConnections.add(connKey);
        }
      }
    }
  }

  // Add end node
  if (steps.length > 1) {
    steps.push({
      id: "end",
      name: "End",
      type: "end",
    });
    
    // Connect last step to end
    const lastStep = stepOrder.length > 0 ? stepOrder[stepOrder.length - 1].id : null;
    if (lastStep) {
      connections.push({ from: lastStep, to: "end" });
    }
  }

  return { steps, connections };
};

// Convert parsed workflow to ReactFlow nodes and edges
const createFlowElements = (
  steps: WorkflowStep[],
  connections: WorkflowConnection[]
): { nodes: Node[]; edges: Edge[] } => {
  // Build a graph to understand the flow structure
  const graph = new Map<string, string[]>();
  connections.forEach(conn => {
    if (!graph.has(conn.from)) {
      graph.set(conn.from, []);
    }
    graph.get(conn.from)!.push(conn.to);
  });

  // Assign vertical positions based on topological order
  const verticalPositions = new Map<string, number>();
  const visited = new Set<string>();
  let currentY = 0;

  const assignPosition = (stepId: string) => {
    if (visited.has(stepId)) return;
    visited.add(stepId);
    
    verticalPositions.set(stepId, currentY);
    currentY++;
    
    const children = graph.get(stepId) || [];
    children.forEach(childId => assignPosition(childId));
  };

  // Start from the "start" node
  assignPosition("start");

  const nodes: Node[] = steps.map((step) => {
    let nodeType = "default";
    let style: any = {
      background: "#FFF",
      border: "2px solid #E0E0E0",
      borderRadius: "6px",
      padding: "12px 16px",
      fontFamily: "var(--theo-font-sans)",
      fontSize: "13px",
      fontWeight: 500,
      color: "#212121",
      minWidth: 120,
    };

    switch (step.type) {
      case "start":
        style.background = "#E8F5E9";
        style.borderColor = "#4CAF50";
        style.color = "#2E7D32";
        break;
      case "end":
        style.background = "#FFEBEE";
        style.borderColor = "#F44336";
        style.color = "#C62828";
        break;
      case "condition":
        style.background = "#FFF3E0";
        style.borderColor = "#FF9800";
        style.color = "#E65100";
        style.borderRadius = "50px";
        break;
      case "action":
        style.background = "#E3F2FD";
        style.borderColor = "#2196F3";
        style.color = "#1565C0";
        break;
      case "step":
        style.background = "#F3E5F5";
        style.borderColor = "#9C27B0";
        style.color = "#6A1B9A";
        break;
    }

    // Calculate position for top-to-bottom flow with branching
    const centerX = 300; // Center X position for main flow
    const conditionOffsetX = -200; // Offset for conditions (to the left)
    const verticalSpacing = 140; // Increased vertical spacing between nodes
    
    let x = centerX;
    let y = 50;
    
    const verticalPos = verticalPositions.get(step.id) ?? 0;
    y = verticalPos * verticalSpacing + 50;
    
    if (step.type === "start" || step.type === "end") {
      x = centerX;
    } else if (step.type === "condition") {
      // Position conditions to the left
      x = centerX + conditionOffsetX;
    } else {
      // Position actions at center
      x = centerX;
    }
    
    return {
      id: step.id,
      type: nodeType,
      position: {
        x,
        y,
      },
      data: {
        label: (
          <Box>
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: 13,
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {step.name}
            </Typography>
            {step.details && (
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 11,
                  color: "#757575",
                  mt: 0.5,
                  textAlign: "center",
                }}
              >
                {step.details}
              </Typography>
            )}
          </Box>
        ),
      },
      style,
    };
  });

  const edges: Edge[] = connections.map((conn, index) => ({
    id: `edge-${index}`,
    source: conn.from,
    target: conn.to,
    type: "smoothstep",
    animated: true,
    style: { stroke: "#705D4A", strokeWidth: 2 },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: "#705D4A",
    },
    label: conn.condition
      ? conn.condition.length > 20
        ? conn.condition.substring(0, 20) + "..."
        : conn.condition
      : undefined,
    labelStyle: {
      fill: "#705D4A",
      fontWeight: 500,
      fontFamily: "var(--theo-font-sans)",
      fontSize: 11,
    },
  }));

  return { nodes, edges };
};

const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({ pythonCode }) => {
  const { nodes, edges } = useMemo(() => {
    const { steps, connections } = parseWorkflowCode(pythonCode);
    return createFlowElements(steps, connections);
  }, [pythonCode]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "800px",
        border: "1px solid #E0E0E0",
        borderRadius: 1,
        backgroundColor: "#FAFAFA",
        overflow: "hidden",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
      >
        <Background color="#E0E0E0" gap={16} />
        <Controls
          style={{
            backgroundColor: "#FFF",
            border: "1px solid #E0E0E0",
          }}
        />
        <MiniMap
          style={{
            backgroundColor: "#FFF",
            border: "1px solid #E0E0E0",
          }}
          nodeColor={(node) => {
            if (node.data?.label?.props?.children?.props?.children?.props?.children === "Start") {
              return "#4CAF50";
            }
            if (node.data?.label?.props?.children?.props?.children?.props?.children === "End") {
              return "#F44336";
            }
            return "#9C27B0";
          }}
        />
      </ReactFlow>
    </Box>
  );
};

export default WorkflowDiagram;

